/**
 * How a price-book item is priced (F3): by a markup on cost, by a margin, or by
 * a fixed sell price. The server accepts exactly one of the three, so the form
 * works out all three fields from the one mode the user chose and never sends
 * a markup alongside a non-zero sell or margin (that is a 422).
 */
import { markupBpToMarginBp, sellFromCost, sellFromMarkup } from "./pricing";

export type PriceMode = "markup" | "margin" | "fixed";

/** What the form prefills from. Markup wins, then margin, then a fixed price. */
export function priceModeOf(item: {
  markup_bp?: number | null;
  margin_bp?: number | null;
  sell_cents?: number | null;
}): PriceMode {
  if (item.markup_bp !== null && item.markup_bp !== undefined) return "markup";
  if (item.margin_bp !== null && item.margin_bp !== undefined) return "margin";
  if ((item.sell_cents ?? 0) > 0) return "fixed";
  return "margin";
}

/** 4000 to "40", 3750 to "37.5". Empty for nothing. */
export function bpToPctText(bp: number | null | undefined): string {
  if (bp === null || bp === undefined) return "";
  return String(Math.round(bp) / 100);
}

/** "+40%" from 4000, for a list row. */
export function markupTag(bp: number): string {
  return `+${bpToPctText(bp)}%`;
}

/**
 * "40" or "37.5%" to basis points. Blank is `{ bp: null, valid: true }` (nothing
 * chosen); anything that is not a number from 0 to `maxPct` is not valid.
 */
export function pctToBp(text: string, maxPct: number): { bp: number | null; valid: boolean } {
  const clean = text.replace(/[%\s]/g, "");
  if (clean === "") return { bp: null, valid: true };
  const value = Number(clean);
  if (!Number.isFinite(value) || value < 0 || value > maxPct) return { bp: null, valid: false };
  return { bp: Math.round(value * 100), valid: true };
}

/** The server's ceilings: markup up to 1000%, margin below 99%. */
export const MAX_MARKUP_PCT = 1000;
export const MAX_MARGIN_PCT = 98.99;

export type PriceFields = { sell_cents: number; margin_bp: number | null; markup_bp: number | null };

/**
 * The three pricing fields for the chosen mode. The other two are cleared
 * (0 / null) rather than omitted, so switching mode on an existing item
 * actually clears the old rule.
 */
export function priceFields(
  mode: PriceMode,
  input: { sellCents: number; marginText: string; markupText: string },
): PriceFields {
  if (mode === "markup") {
    return { sell_cents: 0, margin_bp: null, markup_bp: pctToBp(input.markupText, MAX_MARKUP_PCT).bp };
  }
  if (mode === "margin") {
    return { sell_cents: 0, margin_bp: pctToBp(input.marginText, MAX_MARGIN_PCT).bp, markup_bp: null };
  }
  return { sell_cents: input.sellCents, margin_bp: null, markup_bp: null };
}

/** What a markup sells at, and the margin that works out to. */
export function markupPreview(costCents: number, markupBp: number): { sellCents: number; marginBp: number } {
  return { sellCents: sellFromMarkup(costCents, markupBp), marginBp: markupBpToMarginBp(markupBp) };
}

/** What a margin sells at. */
export function marginPreview(costCents: number, marginBp: number): number {
  return sellFromCost(costCents, marginBp);
}
